import {datadogLogs} from '@datadog/browser-logs';
import {datadogRum} from '@datadog/browser-rum';

import config, {envIsDev} from '../../config';
import {initDatadogLogs} from './datadogLogs';
import {initDatadogRum} from './datadogRum';

let isDatadogEnabledAtRuntime = true;

// This flag is set elsewhere to indicate that datadog should be enabled
declare global {
  interface Window {
    DatadogEnabled?: boolean;
  }
}

/**
 * This should be called ONCE at application startup.
 * This is currently called in the consentAwareAnalyticSetup.tsx file.
 */
export function initDatadog() {
  if (!datadogEnabled()) {
    return;
  }
  initDatadogLogs();
  initDatadogRum();
}

/**
 * Disables Datadog logging and RUM by revoking tracking consent.
 * This is typically called when an admin user is detected.
 * Can be called at any time to halt Datadog tracking.
 */
export function disableDatadog() {
  isDatadogEnabledAtRuntime = false;

  // Halts Datadog RUM by revoking tracking consent. Can be done at any time.
  // This is a bit of a hack, but should work pretty reliably. The main way this
  // function will be called is if the React tree detects that the user is an
  // admin.
  datadogLogs.setTrackingConsent('not-granted');
  datadogRum.setTrackingConsent('not-granted');
}

/**
 * Checks for debug mode, development environment, analytics settings, private environments,
 * and runtime disablement (e.g., admin users).
 *
 * @returns True if Datadog should be enabled, false otherwise
 */
export function datadogEnabled() {
  /* eslint-disable curly */
  // Can set the DATADOG_DEBUG config flag to force-enable Datadog logging. This
  // is for debugging Datadog's behavior in development environments and should
  // not be otherwise used.
  if (config.DATADOG_DEBUG) return true;

  // If we are in a development environment, don't log to Datadog.
  if (envIsDev) return false;

  // If analytics are disabled in this environment, don't log to Datadog.
  if (config.ANALYTICS_DISABLED) return false;

  if (typeof window === 'undefined') return false;

  // If in a private environment (which are private?) don't log to Datadog.
  if (window.CONFIG?.ENVIRONMENT_IS_PRIVATE) return false;

  // Finally, if DataDog just isn't enabled, don't log to it.
  if (!window.DatadogEnabled) return false;

  // If we got this far, the only thing that can indicate whether or not DD is
  // running is if it's been specifically turned off via `disableDatadog` later
  // in this file. This can happen because the user is detected to be an admin.
  return isDatadogEnabledAtRuntime;
  /* eslint-enable curly */
}
